import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/layout/Navbar';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import ROICalculator from './pages/ROICalculator';
import AIReadinessAssessment from './pages/AIReadinessAssessment';
import AIExplainers from './pages/AIExplainers';
import Frameworks from './pages/Frameworks';
import TechnologySecurity from './pages/TechnologySecurity';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import EthicsPolicy from './pages/EthicsPolicy';
import AccessibilityStatement from './pages/AccessibilityStatement';
import ModernSlaveryStatement from './pages/ModernSlaveryStatement';
import CookiePolicy from './pages/CookiePolicy';
import NotFound from './pages/NotFound';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';

function AppContent() {
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          const offset = 80;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.state]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      <ScrollToTop />
      <Navbar scrollPosition={scrollPosition} />
      <Routes>
        {/* Main Pages - Clean URLs */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />

        {/* Tools Section */}
        <Route path="/tools/roi-calculator" element={<ROICalculator />} />
        <Route path="/tools/ai-readiness-assessment" element={<AIReadinessAssessment />} />

        {/* Government Section */}
        <Route path="/frameworks" element={<Frameworks />} />
        <Route path="/technology-security" element={<TechnologySecurity />} />

        {/* Resources Section */}
        <Route path="/resources/ai-explainers" element={<AIExplainers />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Legal Pages */}
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
        <Route path="/legal/terms-of-service" element={<TermsOfService />} />
        <Route path="/legal/ethics-policy" element={<EthicsPolicy />} />
        <Route path="/legal/accessibility" element={<AccessibilityStatement />} />
        <Route path="/legal/modern-slavery" element={<ModernSlaveryStatement />} />

        {/* Legacy URL Redirects */}
        <Route path="/roi-calculator" element={<Navigate to="/tools/roi-calculator" replace />} />
        <Route path="/ai-readiness" element={<Navigate to="/tools/ai-readiness-assessment" replace />} />
        <Route path="/ai-explainers" element={<Navigate to="/resources/ai-explainers" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/legal/privacy-policy" replace />} />
        <Route path="/terms-of-service" element={<Navigate to="/legal/terms-of-service" replace />} />
        <Route path="/ethics-policy" element={<Navigate to="/legal/ethics-policy" replace />} />

        {/* 404 Page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <CookieConsent />
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <div className="relative w-full overflow-x-hidden">
          <AppContent />
        </div>
      </Router>
    </HelmetProvider>
  );
}